import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useQuery } from "@apollo/react-hooks";
import { Alert, AutoComplete, Button, Input, Select } from "antd";
import { connect } from "formik";
import gql from "fraql";
import get from "lodash/get";
import isPlainObject from "lodash/isPlainObject";
import omit from "lodash/omit";
import sortBy from "lodash/sortBy";
import ENV from "../../constants/envConstants";
import isBlank from "../../utils/isBlank";
import FormFieldFormItem from "./FormFieldFormItem";
import FormFieldReadOnlyContent from "./FormFieldReadOnlyFields/FormFieldReadOnlyContent";
import ReadOnlyText from "./FormFieldReadOnlyFields/ReadOnlyText";
import "./FormFieldAddress.scss";

const { Option: AutoCompleteOption } = AutoComplete;
const { Option: SelectOption } = Select;

const GOOGLE_PLACES_API_URL = "https://maps.googleapis.com/maps/api/js";

const GET_COUNTRY_OPTIONS = gql`
  query CountryList_By_Tenant_ForAddress($tenantId: String!) {
    Country(where: { tenantId: { _eq: $tenantId } }) {
      id
      code
      name
    }
  }
`;

let googlePlacesScriptPromise;

function getAddressCountryCode(value) {
  const addressComponents = get(value, "address_components", []);
  const countryComponent = addressComponents.find(component => {
    const types = get(component, "types", []);
    return types.includes("country");
  });

  return String(get(countryComponent, "short_name", "")).trim().toLowerCase();
}

function hasPlacesLibrary() {
  return !!(
    window.google &&
    window.google.maps &&
    window.google.maps.places &&
    window.google.maps.places.AutocompleteService &&
    window.google.maps.places.PlacesService
  );
}

function waitForPlacesLibrary(timeoutMs = 3000, intervalMs = 100) {
  return new Promise((resolve, reject) => {
    const endTime = Date.now() + timeoutMs;

    const checkPlacesLibrary = () => {
      if (hasPlacesLibrary()) {
        resolve();
        return;
      }

      if (Date.now() >= endTime) {
        reject(new Error("Google Maps loaded but Places library is unavailable."));
        return;
      }

      setTimeout(checkPlacesLibrary, intervalMs);
    };

    checkPlacesLibrary();
  });
}

function ensurePlacesLibraryLoaded() {
  if (hasPlacesLibrary()) {
    return Promise.resolve();
  }

  if (window.google && window.google.maps && window.google.maps.importLibrary) {
    return window.google.maps
      .importLibrary("places")
      .then(() => waitForPlacesLibrary())
      .catch(() => waitForPlacesLibrary());
  }

  return waitForPlacesLibrary();
}

function loadGooglePlacesScript(apiKey) {
  if (googlePlacesScriptPromise) {
    return googlePlacesScriptPromise;
  }

  googlePlacesScriptPromise = new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("window is not available."));
      return;
    }

    if (hasPlacesLibrary()) {
      resolve(window.google);
      return;
    }

    const scriptId = "google-maps-places-script";
    const existingScript = document.getElementById(scriptId);

    if (existingScript) {
      existingScript.addEventListener("load", () => {
        ensurePlacesLibraryLoaded()
          .then(() => resolve(window.google))
          .catch(reject);
      });
      existingScript.addEventListener("error", () => reject(new Error("Google Maps script failed to load.")));
      return;
    }

    const script = document.createElement("script");
    script.id = scriptId;
    script.src = `${GOOGLE_PLACES_API_URL}?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      ensurePlacesLibraryLoaded()
        .then(() => resolve(window.google))
        .catch(reject);
    };
    script.onerror = () => reject(new Error("Google Maps script failed to load."));

    document.body.appendChild(script);
  });

  return googlePlacesScriptPromise;
}

function sanitizePlaceResult(placeResult) {
  if (!placeResult) {
    return null;
  }

  try {
    return JSON.parse(JSON.stringify(placeResult));
  } catch (error) {
    console.error("Failed to serialise Google Places result.", error);
    return null;
  }
}

function getAddressDisplayText(value) {
  if (isBlank(value)) {
    return "";
  }

  if (isPlainObject(value)) {
    return get(value, "formatted_address") || get(value, "description") || "";
  }

  return String(value);
}

function FormFieldAddress(props) {
  const {
    name,
    meta,
    disabled,
    formik: { values, setFieldTouched, setFieldValue },
    readOnly,
  } = props;

  const googlePlacesApiKey = ENV.GOOGLE_PLACES_API_KEY;
  const fieldValue = get(values, name, null);
  const displayDefaultLabel = get(meta, "displayDefaultLabel", true);
  const tenantId = get(meta, "formTenantId", null);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const [predictions, setPredictions] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedCountryCode, setSelectedCountryCode] = useState("");
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const predictionDebounceRef = useRef(null);

  const { loading: countryListLoading, data: countryData } = useQuery(GET_COUNTRY_OPTIONS, {
    variables: { tenantId },
    skip: !tenantId,
  });

  const isApiKeyConfigured = !!googlePlacesApiKey;

  let placeholder = get(meta, "placeholder", null);

  if (!placeholder) {
    placeholder = "Search for an address";
  }

  const countryOptions = useMemo(() => {
    if (countryListLoading) {
      return [];
    }

    const countryQueryList = get(countryData, "Country", []);
    const sourceList = countryQueryList.length === 0 ? JSON.parse(localStorage.getItem("countryList") || "[]") : countryQueryList;

    // Duplicate country codes can cause Select to render unstable results in search mode.
    const dedupedOptionsByCode = sourceList
      .filter(country => !isBlank(country.code) && !isBlank(country.name))
      .reduce((optionMap, country) => {
        const code = String(country.code).trim().toLowerCase();
        const label = String(country.name).trim();

        if (optionMap[code]) {
          return optionMap;
        }

        return {
          ...optionMap,
          [code]: {
            value: code,
            label,
          },
        };
      }, {});

    return sortBy(Object.values(dedupedOptionsByCode), option => option.label);
  }, [countryData, countryListLoading]);

  const selectedCountryLabel = useMemo(() => {
    const selectedCountry = countryOptions.find(option => option.value === selectedCountryCode);
    return get(selectedCountry, "label", "");
  }, [countryOptions, selectedCountryCode]);

  useEffect(() => {
    setSearchText(getAddressDisplayText(fieldValue));
  }, [fieldValue]);

  useEffect(() => {
    const savedCountryCode = getAddressCountryCode(fieldValue);

    if (savedCountryCode) {
      setSelectedCountryCode(savedCountryCode);
    }
  }, [fieldValue]);

  useEffect(() => {
    if (!isApiKeyConfigured) {
      return undefined;
    }

    let isMounted = true;

    loadGooglePlacesScript(googlePlacesApiKey)
      .then(() => {
        if (!isMounted || !hasPlacesLibrary()) {
          return;
        }

        autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
        placesServiceRef.current = new window.google.maps.places.PlacesService(document.createElement("div"));
        setIsGoogleLoaded(true);
      })
      .catch(error => {
        setIsGoogleLoaded(false);
        console.error("Failed to initialise Google Places.", error);
      });

    return () => {
      isMounted = false;

      if (predictionDebounceRef.current) {
        clearTimeout(predictionDebounceRef.current);
      }
    };
  }, [googlePlacesApiKey, isApiKeyConfigured]);

  const handleSearch = useCallback(
    value => {
      setSearchText(value);

      if (!isGoogleLoaded || !autocompleteServiceRef.current || !selectedCountryCode) {
        return;
      }

      if (predictionDebounceRef.current) {
        clearTimeout(predictionDebounceRef.current);
      }

      if (isBlank(value)) {
        setPredictions([]);
        setFieldValue(name, null, true);
        setFieldTouched(name, true);
        return;
      }

      predictionDebounceRef.current = setTimeout(() => {
        autocompleteServiceRef.current.getPlacePredictions(
          {
            input: value,
            types: ["address"],
            componentRestrictions: { country: selectedCountryCode },
          },
          predictionResults => {
            setPredictions(predictionResults || []);
          },
        );
      }, 300);
    },
    [isGoogleLoaded, name, selectedCountryCode, setFieldTouched, setFieldValue],
  );

  const handleCountryChange = useCallback(
    countryCode => {
      const currentValueCountryCode = getAddressCountryCode(fieldValue);

      setSelectedCountryCode(countryCode || "");
      setPredictions([]);
      setSearchText("");

      if (currentValueCountryCode && currentValueCountryCode !== countryCode) {
        setFieldValue(name, null, true);
        setFieldTouched(name, true);
      }
    },
    [fieldValue, name, setFieldTouched, setFieldValue],
  );

  const handleSelect = useCallback(
    selectedValue => {
      const selectedPrediction = predictions.find(prediction => prediction.description === selectedValue);
      const placeId = get(selectedPrediction, "place_id", null);

      if (!placeId || !placesServiceRef.current || !window.google || !window.google.maps) {
        return;
      }

      placesServiceRef.current.getDetails(
        {
          placeId,
          fields: [
            "address_components",
            "adr_address",
            "formatted_address",
            "name",
            "place_id",
            "types",
            "url",
            "utc_offset_minutes",
            "vicinity",
            "website",
          ],
        },
        placeResult => {
          const sanitizedPlaceResult = sanitizePlaceResult(placeResult);

          if (!sanitizedPlaceResult) {
            return;
          }

          setFieldValue(name, sanitizedPlaceResult, true);
          setFieldTouched(name, true);
          setSearchText(getAddressDisplayText(sanitizedPlaceResult));
          setPredictions([]);
        },
      );
    },
    [name, predictions, setFieldTouched, setFieldValue],
  );

  const handleClear = useCallback(() => {
    setSearchText("");
    setPredictions([]);
    setFieldValue(name, null, true);
    setFieldTouched(name, true);
  }, [name, setFieldTouched, setFieldValue]);

  const predictionOptions = useMemo(() => {
    return predictions.map(prediction => {
      const placeId = get(prediction, "place_id", "");
      const description = get(prediction, "description", "");

      return (
        <AutoCompleteOption key={placeId} value={description}>
          {description}
        </AutoCompleteOption>
      );
    });
  }, [predictions]);

  function getReadOnlyContent() {
    const displayValue = getAddressDisplayText(fieldValue);

    if (isBlank(displayValue)) {
      return null;
    }

    return <ReadOnlyText>{displayValue}</ReadOnlyText>;
  }

  if (readOnly) {
    return <FormFieldReadOnlyContent name={name} meta={meta} content={getReadOnlyContent()} />;
  }

  const isCountryDisabled = disabled || (countryListLoading && countryOptions.length === 0);
  const isAddressDisabled = disabled || !isApiKeyConfigured || !isGoogleLoaded || !selectedCountryCode;
  const hasSelectedValidAddress = isPlainObject(fieldValue) && !isBlank(get(fieldValue, "place_id", ""));
  const showAddressSelectionWarning =
    !disabled && !!selectedCountryCode && !isBlank(searchText) && !hasSelectedValidAddress;
  const apiKeyHelpText = isApiKeyConfigured
    ? null
    : "Google Places API key is not configured. Set REACT_APP_GOOGLE_PLACES_API_KEY in your environment.";
  let notFoundContent = "Loading address lookup...";

  if (!selectedCountryCode) {
    notFoundContent = "Select a country first";
  } else if (isGoogleLoaded) {
    notFoundContent = "No matching addresses";
  }

  return (
    <FormFieldFormItem
      {...omit(props, ["formik"])}
      className="form-field-address"
      displayDefaultLabel={displayDefaultLabel}
      meta={{ ...meta, helpText: apiKeyHelpText || get(meta, "helpText", "") }}
    >
      <Select
        value={selectedCountryCode || undefined}
        placeholder="Select a country"
        disabled={isCountryDisabled}
        loading={countryListLoading}
        onChange={handleCountryChange}
        onClear={() => handleCountryChange("")}
        allowClear
        showSearch
        filterOption={(input, option) => {
          const optionLabel = String(get(option, "props.children", "")).toLowerCase();
          return optionLabel.includes(String(input).toLowerCase());
        }}
        style={{ width: "100%", marginBottom: 8 }}
      >
        {countryOptions.map(option => (
          <SelectOption key={option.value} value={option.value}>
            {option.label}
          </SelectOption>
        ))}
      </Select>
      <AutoComplete
        value={searchText}
        dataSource={predictionOptions}
        onSearch={handleSearch}
        onSelect={handleSelect}
        disabled={isAddressDisabled}
        placeholder={selectedCountryCode ? `${placeholder}${selectedCountryLabel ? ` in ${selectedCountryLabel}` : ""}` : "Select a country before searching for an address"}
        notFoundContent={notFoundContent}
        optionLabelProp="value"
      >
        <Input />
      </AutoComplete>
      {showAddressSelectionWarning && (
        <Alert
          className="form-field-address__warning"
          type="warning"
          showIcon
          message="Address not yet selected"
          description="Select an address from the Google lookup list to save it, or clear the field if you do not want to provide an address."
        />
      )}
      <Button onClick={handleClear} style={{ marginLeft: 8, marginTop: 8 }} icon="close" disabled={disabled}>
        Clear
      </Button>
    </FormFieldFormItem>
  );
}

FormFieldAddress.propTypes = {
  name: PropTypes.string.isRequired,
  meta: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  formik: PropTypes.object.isRequired,
  readOnly: PropTypes.bool,
};

FormFieldAddress.defaultProps = {
  disabled: false,
  readOnly: false,
};

export default connect(FormFieldAddress);
